import { Box, Typography, useTheme } from '@mui/material'
import { FC, useEffect, useMemo, useRef } from 'react'
import { smoothScrollIntoViewHorizontally } from './smooth-scroll'

export interface Book {
  title: string
  author: string
  words: number
  img?: string | null
}

interface BookTimelineProps {
  books: Book[]
  currentWords: number
  currentLabel: string
  initialScrollDurationMs: number
  stripWidthPx?: number
}

const DEFAULT_STRIP_WIDTH_PX = 9000

// Per-column footprint widths (used both for visual width and overlap detection).
const BOOK_COLUMN_WIDTH_PX = 180
const CURRENT_COLUMN_WIDTH_PX = 240

// Minor tick marks below the axis, one per this many words.
const MINOR_TICK_WORDS = 10_000

// Vertical layout. Axis sits at axisTopPx (computed from currentLevel below).
// Word counts below the axis are rotated (writing-mode: vertical-rl) so they
// take ~16px horizontally instead of ~80px and never collide with each other.
const AXIS_BELOW_PX = 110 // space under the axis for the rotated word-count text
const LABEL_TOP_PADDING_PX = 100 // baseline space above the axis at level 0
const LEVEL_OFFSET_PX = 64 // vertical lift per stack level when labels collide

interface LaidOutBook {
  book: Book
  x: number
  level: number
}

// Greedy first-fit row assignment: walk books sorted by x and put each on
// the lowest row where it doesn't overlap a previously-placed book.
function assignLevels(books: Book[], xFor: (w: number) => number): LaidOutBook[] {
  const ordered = books.map(book => ({ book, x: xFor(book.words) })).sort((a, b) => a.x - b.x)

  const rowRightEdge: number[] = []
  return ordered.map(({ book, x }) => {
    const left = x - BOOK_COLUMN_WIDTH_PX / 2
    const right = x + BOOK_COLUMN_WIDTH_PX / 2
    let level = 0
    while (rowRightEdge[level] != null && rowRightEdge[level] > left) level += 1
    rowRightEdge[level] = right
    return { book, x, level }
  })
}

export const BookTimeline: FC<BookTimelineProps> = ({
  books,
  currentWords,
  currentLabel,
  initialScrollDurationMs,
  stripWidthPx = DEFAULT_STRIP_WIDTH_PX,
}) => {
  const theme = useTheme()
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const currentMarkerRef = useRef<HTMLDivElement | null>(null)

  const maxWords = Math.max(currentWords, ...books.map(b => b.words)) * 1.05 || 200000
  const xFor = (words: number) => (words / maxWords) * stripWidthPx

  const laidOut = useMemo(() => assignLevels(books, xFor), [books, maxWords, stripWidthPx])
  const maxBookLevel = laidOut.length ? Math.max(...laidOut.map(b => b.level)) : -1
  const currentLevel = maxBookLevel + 1 // current marker always above all books

  const axisTopPx = LABEL_TOP_PADDING_PX + currentLevel * LEVEL_OFFSET_PX
  const stripHeightPx = axisTopPx + AXIS_BELOW_PX

  useEffect(() => {
    if (!scrollContainerRef.current || !currentMarkerRef.current) return
    smoothScrollIntoViewHorizontally(
      scrollContainerRef.current,
      currentMarkerRef.current,
      initialScrollDurationMs,
    )
  }, [initialScrollDurationMs])

  const renderConnector = (level: number, color: string, widthPx: number) =>
    level > 0 ? (
      <Box
        sx={{
          position: 'absolute',
          top: axisTopPx - level * LEVEL_OFFSET_PX,
          height: level * LEVEL_OFFSET_PX,
          left: '50%',
          width: `${widthPx}px`,
          backgroundColor: color,
          transform: 'translateX(-50%)',
        }}
      />
    ) : null

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        overflowX: 'auto',
        overflowY: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ position: 'relative', width: stripWidthPx, height: stripHeightPx }}>
        {/* axis line */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: axisTopPx,
            height: '2px',
            backgroundColor: theme.palette.divider,
          }}
        />

        {/* minor ticks every MINOR_TICK_WORDS, drawn below the axis */}
        {Array.from({ length: Math.floor(maxWords / MINOR_TICK_WORDS) + 1 }).map((_, i) => (
          <Box
            key={`minor-${i}`}
            sx={{
              position: 'absolute',
              top: axisTopPx + 2,
              left: xFor(i * MINOR_TICK_WORDS),
              width: '1px',
              height: 5,
              backgroundColor: theme.palette.text.disabled,
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
            }}
          />
        ))}

        {laidOut.map(({ book, x, level }) => {
          const labelBottomPx = axisTopPx - level * LEVEL_OFFSET_PX
          return (
            <Box
              key={`${book.title}-${book.author}`}
              sx={{
                position: 'absolute',
                left: x,
                top: 0,
                width: BOOK_COLUMN_WIDTH_PX,
                height: '100%',
                transform: 'translateX(-50%)',
                pointerEvents: 'none',
              }}
            >
              {/* Label block (img + title + author), anchored above the lifted baseline */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: stripHeightPx - labelBottomPx + 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.25,
                }}
              >
                {book.img && (
                  <Box
                    component='img'
                    src={book.img}
                    alt=''
                    sx={{ maxHeight: 64, maxWidth: 80, objectFit: 'contain' }}
                  />
                )}
                <Typography variant='subtitle2' align='center' sx={{ lineHeight: 1.2 }}>
                  {book.title}
                </Typography>
                <Typography variant='caption' color='text.secondary' align='center'>
                  {book.author}
                </Typography>
              </Box>

              {/* Connector — bridges lifted label to the axis tick */}
              {renderConnector(level, theme.palette.divider, 1)}

              {/* Tick at axis */}
              <Box
                sx={{
                  position: 'absolute',
                  top: axisTopPx - 7,
                  left: '50%',
                  width: 2,
                  height: 14,
                  backgroundColor: theme.palette.text.secondary,
                  transform: 'translateX(-50%)',
                }}
              />

              {/* Word count below axis — rotated to read top-to-bottom */}
              <Typography
                variant='caption'
                sx={{
                  position: 'absolute',
                  top: axisTopPx + 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  writingMode: 'vertical-rl',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}
              >
                {book.words.toLocaleString()} words
              </Typography>
            </Box>
          )
        })}

        {/* Current docs marker — always at currentLevel = maxBookLevel + 1 */}
        <Box
          ref={currentMarkerRef}
          sx={{
            position: 'absolute',
            left: xFor(currentWords),
            top: 0,
            width: CURRENT_COLUMN_WIDTH_PX,
            height: '100%',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
          }}
        >
          {/* Label pill */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: stripHeightPx - (axisTopPx - currentLevel * LEVEL_OFFSET_PX) + 4,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 700,
                fontSize: 13,
                whiteSpace: 'nowrap',
              }}
            >
              {currentLabel}
            </Box>
          </Box>

          {/* Connector */}
          {renderConnector(currentLevel, theme.palette.primary.main, 2)}

          {/* Tick at axis (taller + accent) */}
          <Box
            sx={{
              position: 'absolute',
              top: axisTopPx - 14,
              left: '50%',
              width: 3,
              height: 28,
              backgroundColor: theme.palette.primary.main,
              transform: 'translateX(-50%)',
            }}
          />

          {/* Word count below axis — rotated to read top-to-bottom */}
          <Typography
            variant='caption'
            sx={{
              position: 'absolute',
              top: axisTopPx + 12,
              left: '50%',
              transform: 'translateX(-50%)',
              writingMode: 'vertical-rl',
              whiteSpace: 'nowrap',
              lineHeight: 1,
              color: theme.palette.primary.main,
              fontWeight: 700,
            }}
          >
            {currentWords.toLocaleString()} words
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
